import { useMemo } from 'react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { Table as BsTable } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';
import DataTablePagination from './DataTablePagination';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';

const DataTable = ({
    columns,
    data = [],
    loading = false,
    // Server-side pagination props
    page = 1,
    pageSize = 20,
    totalCount = 0,
    totalPages = 1,
    onPageChange,
    onPageSizeChange,
    // Sorting
    sorting = [],
    onSortingChange,
    // Table options
    emptyTitle,
    emptyDescription,
    onRowClick,
    striped = true,
    hover = true,
}) => {
    const tableColumns = useMemo(() => columns, [columns]);

    const table = useReactTable({
        data,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        pageCount: totalPages,
        state: {
            sorting,
            pagination: { pageIndex: page - 1, pageSize },
        },
        onSortingChange,
    });

    if (loading && data.length === 0) {
        return <LoadingSpinner />;
    }

    if (!loading && data.length === 0) {
        return <EmptyState title={emptyTitle} description={emptyDescription} />;
    }

    return (
        <div className="data-table-wrapper">
            <div className="table-responsive">
                <BsTable striped={striped} hover={hover} className="align-middle mb-0 data-table">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => onRowClick?.(row.original)}
                                className={onRowClick ? 'cursor-pointer' : ''}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </BsTable>
            </div>

            {onPageChange && totalPages > 0 && (
                <DataTablePagination
                    page={page}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                />
            )}
        </div>
    );
};

export default DataTable;
